import { CloseOutlined } from '@ant-design/icons';
import { Button, Modal } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close, LinkOutlined } from '@mui/icons-material';
import { Row } from 'antd';
import { get, find } from 'lodash';
import React, { useEffect, useMemo, useRef, useState, useCallback,memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllBook } from 'store/books/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import styled from 'styled-components';
import { BACK_TO_BOOK } from 'utils/config/reference';
import COLOR from 'utils/constants/color';
import { ebookConfigDinamic, generateUrlForNonEbook } from 'utils/lib/ebookHelper';
import { disableBackWrad, disableForWard } from '../helper';
import BookImage from './bookImage';
import { useCurrentUser } from 'store/users/selectors';
import AssignmentsComponent from '../../../assignments';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';

const BalconPreview = ({
  open,
  handleClose,
  selectedItems,
  CHAPTER_ID = '33',
  styles,
  extensionActivity = '',
}) => {
  const showAlert = useAlert();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const backBtnRef = useRef();
  const forwardBtnRef = useRef();
  const imageCacheRef = useRef(new Map()); // Use ref instead of state
  const preloadingBatchRef = useRef(false); // Use ref instead of state
  const currentBatchRef = useRef(0); // Use ref instead of state
  
  const [page, setPage] = useState('');
  // Reset cache when component opens or chapter changes
  useEffect(() => {
    if (open) {
      // Reset cache when modal opens or chapter changes
      imageCacheRef.current.clear();
      currentBatchRef.current = 0;
      preloadingBatchRef.current = false;
      setIsAllImagesCached(false);
    }
  }, [open, CHAPTER_ID]);

  // for fetch Book Content
  const booksAllList = useAllBook();
  const bookUnitList = useBookUnits();
  const data = useMemo(() => ebookConfigDinamic(CHAPTER_ID, booksAllList), [CHAPTER_ID, booksAllList]);
  const content = useMemo(() => data?.content || [], [data]);
  const BALCON_PAGES = useMemo(() => 
    get(find(bookUnitList, ['bookId', CHAPTER_ID]), 'pages', []),
    [bookUnitList, CHAPTER_ID]
  );
  const [isAllImagesCached, setIsAllImagesCached] = useState(false);

  const getSelectedItem = useMemo(() => {
    if (BALCON_PAGES) {
      const filteredData = BALCON_PAGES?.find((item) => item?.id === selectedItems?.id);
      return filteredData;
    }
    return {};
  }, [selectedItems, BALCON_PAGES]);

  const checkPageExists = useCallback((startPage) => {
    const bk = find(booksAllList, ['id', CHAPTER_ID]);
    return bk ? find(bk.pages, (k) => k.pageId == startPage) : false;
  }, [booksAllList, CHAPTER_ID]);

  useEffect(() => {
    if (extensionActivity?.linkFromEbook && currentUser?.role === 'student') {
      setPage(extensionActivity?.linkFromEbook);
    } else if (getSelectedItem?.start && getSelectedItem?.start !== page) {
      const isExist = checkPageExists(getSelectedItem?.start);
      if (!isExist) {
        showAlert('Coming Soon', 'info');
        handleClose();
        return;
      }
      setPage(getSelectedItem.start);
    }
  }, [getSelectedItem?.start, extensionActivity?.linkFromEbook, currentUser?.role]);

  const getSrcPage = useMemo(() => {
    let dataPage = find(content, (x) => Number(x.pageId) === Number(page));
    if (dataPage) {
      return dataPage.src;
    }
  }, [content, page]);

  // Get current page index to determine which batch we're in
  const currentPageIndex = useMemo(() => {
    return content.findIndex((item) => item.pageId == Number(page));
  }, [content, page]);

  // Determine which batch the current page belongs to
  const getCurrentBatch = useMemo(() => {
    return Math.floor(currentPageIndex / 5);
  }, [currentPageIndex]);

  // Preload images in batches of 5
  const preloadBatch = useCallback(async (batchNumber) => {
    if (preloadingBatchRef.current || !content.length) return;

    preloadingBatchRef.current = true;
    const startIndex = batchNumber * 5;
    const endIndex = Math.min(startIndex + 5, content.length);
    const batchItems = content.slice(startIndex, endIndex);

    const preloadPromises = batchItems.map((item) => {
      return new Promise((resolve) => {
        if (imageCacheRef.current.has(item.src)) {
          resolve(item.src);
          return;
        }

        const img = new Image();
        img.onload = () => {
          imageCacheRef.current.set(item.src, img);
          resolve(item.src);
        };
        img.onerror = () => {
          console.warn(`Failed to cache image: ${item.src}`);
          resolve(item.src);
        };
        img.src = item.src;
      });
    });

    await Promise.all(preloadPromises);
    currentBatchRef.current = batchNumber;
    preloadingBatchRef.current = false;

    // Check if all images are cached
    const totalBatches = Math.ceil(content.length / 5);
    if (imageCacheRef.current.size >= content.length) {
      setIsAllImagesCached(true);
    }
  }, [content]);

  // Initial preload and batch management
  useEffect(() => {
    if (content.length > 0 && page) {
      // Always preload first batch
      if (currentBatchRef.current === 0 && imageCacheRef.current.size === 0) {
        preloadBatch(0);
      }

      // Preload current batch if not already cached
      const requiredBatch = getCurrentBatch;
      if (requiredBatch !== currentBatchRef.current && requiredBatch >= 0) {
        preloadBatch(requiredBatch);

        // Also preload next batch for smoother navigation
        if (requiredBatch + 1 < Math.ceil(content.length / 5)) {
          setTimeout(() => preloadBatch(requiredBatch + 1), 100);
        }
      }
    }
  }, [content, getCurrentBatch, page]);

  const generateUrl = useMemo(() => {
    const backToLabel = BACK_TO_BOOK[CHAPTER_ID];
    const bookitem = find(
      BALCON_PAGES,
      (k) => parseInt(k.start) <= page && parseInt(k.finish) >= page,
    );

    const url = generateUrlForNonEbook(
      bookitem?.id,
      page,
      null,
      null,
      backToLabel,
      null,
      CHAPTER_ID,
    );
    return url;
  }, [page, BALCON_PAGES, CHAPTER_ID]);

  const redirectPage = useCallback(() => {
    handleClose();
    navigate(generateUrl);
  }, [handleClose, navigate, generateUrl]);

  const handlePageChange = useCallback((direction) => {
    if (direction === 'next') {
      setPage(parseInt(page, 10) + 1);
    } else {
      setPage(parseInt(page, 10) - 1);
    }
  }, [page]);

  const checkKey = useCallback((e) => {
    if (e) {
      e = e || window.event;
      if (e.keyCode == '37') {
        if (backBtnRef.current && !backBtnRef.current.disabled) {
          backBtnRef.current.click();
        }
      } else if (e.keyCode == '39') {
        if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
          forwardBtnRef.current.click();
        }
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', checkKey);
    return () => {
      document.removeEventListener('keydown', checkKey);
    };
  }, []);

  return (
    <>
      {currentUser?.role === 'teacher' && open && (
        <StyledModal
          open={open}
          onClose={handleClose}
          className={'lessonPlanPopUp main-book-popup'}
        >
          <LearnPlanWrapper>
            <LessionPlanContainer isStudent={currentUser?.role === 'student'}>
              <div className="w-full align-center flex justify-center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LinkOutlined />}
                  onClick={redirectPage}
                  style={{ marginBottom: '10px' }}
                >
                  Go to Page
                </Button>
              </div>

              <CloseBtn
                onClick={() => {
                  handleClose(false);
                }}
              >
                <StyledCloseIcon />
              </CloseBtn>
              <div className="balconPreviewPrintWrapper">
                <PrintWrapper>
                  <ButtonContainer
                    ref={backBtnRef}
                    disabled={disableBackWrad(page, getSelectedItem?.start)}
                    onClick={() => handlePageChange('back')}
                    left={0}
                    className="cstmLeftArow"
                  >
                    <StyledArrowBackIos />
                  </ButtonContainer>
                  <BookImage 
                    src={getSrcPage} 
                    imageCache={imageCacheRef.current} 
                    alt={`Book page ${page}`} 
                  />
                  <ButtonContainer
                    ref={forwardBtnRef}
                    disabled={disableForWard(page, getSelectedItem?.finish)}
                    onClick={() => handlePageChange('next')}
                    right={0}
                    className="cstmRghtArow"
                  >
                    <StyledArrowForwardIos />
                  </ButtonContainer>
                </PrintWrapper>
                <div className="balconPreviewPrintRight">
                  <AssignmentsComponent
                    bookId={CHAPTER_ID}
                    pageId={page}
                    extensionActivityId={extensionActivity?.id}
                    from={'extensionActivity'}
                    renderAssignments={'admin'}
                  />
                </div>
              </div>
            </LessionPlanContainer>
          </LearnPlanWrapper>
        </StyledModal>
      )}

      {currentUser?.role === 'student' && open && (
        <Wrapper
          width={styles.width}
          height={styles.height + 36}
          className={open ? 'main-book-popup printScreenPage setIndexCstm' : ''}
        >
          <CloseButton onClick={handleClose} />
          <HeaderRow
            style={{
              justifyContent: 'space-between',
            }}
          >
            
              <ButtonContainer
                ref={backBtnRef}
                disabled={disableBackWrad(page, getSelectedItem?.start)}
                onClick={() => handlePageChange('back')}
                left={0}
                className="cstmLeftArow"
              >
                <StyledArrowBackIos />
              </ButtonContainer>
            

            <StyledGoToPageButton
              variant="contained"
              color="primary"
              startIcon={<LinkOutlined />}
              onClick={redirectPage}
            >
              Go to Page
            </StyledGoToPageButton>
            
              <ButtonContainer
                ref={forwardBtnRef}
                disabled={disableForWard(page, getSelectedItem?.finish)}
                onClick={() => handlePageChange('next')}
                right={0}
                className="cstmRghtArow"
              >
                <StyledArrowForwardIos />
              </ButtonContainer>
            
          </HeaderRow>
          <BookImage 
            src={getSrcPage} 
            imageCache={imageCacheRef.current} 
            alt={`Book page ${page}`} 
          />
        </Wrapper>
      )}
    </>
  );
};

export default memo(BalconPreview);

// ... (rest of the styled components remain the same)
const PrintWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
`;

const StyledCloseIcon = styled(Close)`
  color: #fff;
  font-weight: bolder;
  font-size: 25px;
`;

const CloseBtn = styled.div`
  background-color: rgb(204, 80, 71);
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  z-index: 1;
`;

const StyledModal = styled(Modal)`
  height: 100%;
  width: 100%;
  background-color: transparent;
  z-index: 1000 !important;
`;

const LearnPlanWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
`;

const LessionPlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  // height: auto;
  max-width: 90%;
  max-height: 90%;
  position: relative;
  padding: 20px;
  background-color: white;
  justify-content: ${(props) => (props.isStudent ? 'center' : 'flex-start')};
  align-items: start;
  @media (max-width: 1024px){
    min-height: 600px;
  }
  @media (max-width: 990px){
    min-height: 550px;
  }
  @media (max-width: 800px){
    min-height: 450px;
  }
    @media (max-width: 700px){
    min-height: 400px;
  }
  @media (max-width: 600px){
    min-height: 350px;
  }
  @media (max-width: 450px){
  min-height: 480px;
  }
`;

const CloseButton = styled(CloseOutlined)`
  position: absolute;
  top: -10px;
  right: -10px;
  cursor: pointer;
  font-size: 16px;
  color: white;
  background: red;
  padding: 5px;
  border-radius: 50%;
`;

const HeaderRow = styled(Row)`
  height: 47px;
  padding: 5px;
  justify-content: space-between;
  display: flex;
  background-color: ${COLOR.white};
  padding-right: 20px;
`;

const StyledGoToPageButton = styled(Button)`
  margin-bottom: 10px;
`;

const StyledArrowBackIos = styled(ArrowBackIos)`
  font-size: 20px;
`;

const StyledArrowForwardIos = styled(ArrowForwardIos)`
  font-size: 20px;
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0px;
  width: ${(p) => p.width}px;
  height: ${(p) => p.height-10}px !important;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
  box-shadow:
    7px -3px 26px 20px rgb(66 66 66),
    -4px -3px 26px 15px rgb(95 109 110);
`;

const ButtonContainer = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.black600};
  cursor: pointer;
  outline: none;
  transition: all 300ms ease;
  width: 40px;
  z-index: 1;
`;